import { useEffect, useState, useMemo } from 'react';
import './styles.css';
import { Layout3Root } from './Layout3Root';
import { VehicleGallery } from './VehicleGallery';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { LayoutHeader as Header } from '@/layouts';
import { LayoutFooter as Footer } from '@/layouts';
import { ServiceModal } from '@/components/ServiceModal';
import { VehicleSection } from '@/components/VehicleSection';
import { extractIdFromSlug, generateVehicleSlug } from '@/lib/slug';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { secureGetItem, secureSetItem } from '@/lib/storage';
import { openWhatsApp } from '@/lib/whatsapp';
import SEOHead from '@/components/SEOHead';
import { generateVehicleStructuredData } from '@/lib/structuredData';
import { 
  MessageCircle, 
  Heart, 
  Calendar, 
  Gauge, 
  Fuel, 
  Settings, 
  Palette,
  Car,
  Shield,
  Phone,
  MapPin,
  Building2,
  ArrowLeft,
  Eye,
  Star,
  CreditCard,
  Send,
  File,
  FilePen,
  Navigation
} from 'lucide-react';

import { useAppData } from '@/contexts/AppDataContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useStore } from '@/contexts/StoreContext';
import { useToast } from '@/hooks/use-toast';
import { requestUserLocation, getDistanceToStore, formatDistance } from '@/lib/geolocation';
import { VehicleNotFound } from '@/components/VehicleNotFound';
import { trackVehicleView, trackLeadSubmission, trackWhatsAppClick, trackAddToFavorites } from '@/lib/analytics';
import { openStoreDirections } from '@/lib/storeMapUrls';

// Lead Form Component (for API type and message type)
const LeadForm = ({ vehicle, config, buttonColor, store, serviceType = 'message', serviceTitle = 'Envie uma mensagem', service }: { vehicle: any; config: any; buttonColor: string; store: any; serviceType?: string; serviceTitle?: string; service?: any }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price);
  };

  // Pre-fill message with vehicle info on mount
  useEffect(() => {
    let vehicleInfo = '';

    if (serviceType === '65dcb7898eca108d87c91dbc') {
      // Financing service
      vehicleInfo = `Olá,\n\nGostaria de simular um financiamento para o veículo:\n\n${vehicle.brand} ${vehicle.model} ${vehicle.version} ${vehicle.modelYear}\nPreço: ${formatPrice(vehicle.price)}\n\nPor favor, entrem em contato para mais informações sobre as condições de financiamento.`;
    } else {
      // Default message service
      vehicleInfo = `Olá,\n\nTenho interesse no veículo:\n\n${vehicle.brand} ${vehicle.model} ${vehicle.version} ${vehicle.modelYear}\nPreço: ${formatPrice(vehicle.price)}\n\nGostaria de receber mais informações.`;
    }

    setFormData(prev => ({
      ...prev,
      message: vehicleInfo
    }));
  }, [vehicle, serviceType]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const formatPhoneNumber = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 10) {
      return numbers.replace(/(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3');
    } else {
      return numbers.replace(/(\d{2})(\d{5})(\d{0,4})/, '($1) $2-$3');
    }
  };

  const handlePhoneChange = (value: string) => {
    const formatted = formatPhoneNumber(value);
    handleInputChange('phone', formatted);
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      toast({
        title: '⚠️ Campo obrigatório',
        description: 'Por favor, preencha o campo Nome.',
        variant: 'destructive'
      });
      return false;
    }

    if (!formData.email.trim()) {
      toast({
        title: '⚠️ Campo obrigatório',
        description: 'Por favor, preencha o campo E-mail.',
        variant: 'destructive'
      });
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast({
        title: '❌ E-mail inválido',
        description: 'Por favor, insira um endereço de e-mail válido.',
        variant: 'destructive'
      });
      return false;
    }

    if (!formData.phone.trim()) {
      toast({
        title: '⚠️ Campo obrigatório',
        description: 'Por favor, preencha o campo Telefone.',
        variant: 'destructive'
      });
      return false;
    }

    const phoneNumbers = formData.phone.replace(/\D/g, '');
    if (phoneNumbers.length < 10) {
      toast({
        title: '❌ Telefone inválido',
        description: 'Por favor, insira um número de telefone válido com DDD.',
        variant: 'destructive'
      });
      return false;
    }

    if (!formData.message.trim()) {
      toast({
        title: '⚠️ Campo obrigatório',
        description: 'Por favor, preencha o campo Mensagem.',
        variant: 'destructive'
      });
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      // Check if service has API type
      const isApiType = (service?.lead?.type === 'api' || service?.lead?.type === 'email');

      if (isApiType) {
        // Send via API
        const { LEAD } = await import('@/lib/api');

        // Prepare lead data following the correct format
        const leadData = {
          name: formData.name,
          email: formData.email,
          phone: formData.phone.replace(/\D/g, ''), // Remove formatting
          message: formData.message,
          vehicle: `${vehicle.brand} ${vehicle.model} ${vehicle.version ? `${vehicle.version} ` : ' '}${vehicle.manufactureYear}/${vehicle.modelYear}`,
          id: vehicle.id,
          plaque: vehicle.plaque || '',
          brand: vehicle.brand,
          model: vehicle.model,
          version: vehicle.version || '',
          year: `${vehicle.manufactureYear}/${vehicle.modelYear}`,
          price: formatPrice(vehicle.price),
          mileage: `${vehicle.mileage}km`
        };

        const response = await LEAD(serviceType, store, leadData);

        if (response?.status == 200) {
          // Track lead submission
          trackLeadSubmission({
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            message: formData.message,
            serviceType,
            vehicle: {
              id: vehicle.id,
              brand: vehicle.brand,
              model: vehicle.model,
              version: vehicle.version || '',
              year: `${vehicle.manufactureYear}/${vehicle.modelYear}`,
              price: vehicle.price,
              mileage: vehicle.mileage
            }
          });

          toast({
            title: 'Mensagem enviada!',
            description: 'Entraremos em contato em breve.',
          });

          // Reset form
          setFormData({
            name: '',
            email: '',
            phone: '',
            message: `Olá,\n\nTenho interesse no veículo:\n\n${vehicle.brand} ${vehicle.model} ${vehicle.version} ${vehicle.modelYear}\nPreço: ${formatPrice(vehicle.price)}\n\nGostaria de receber mais informações.`
          });
        } else{
          toast({
            title: 'Erro ao enviar',
            description: 'Tente novamente.',
            variant: 'destructive'
          });
        }
      } else {
        // Send via WhatsApp (type === 'message')
        toast({
          title: '✅ Redirecionando',
          description: 'Abrindo WhatsApp...',
        });

        const { openWhatsApp } = await import('@/lib/whatsapp');

        if (service?.lead?.ios) {
          // Build message from template (same logic as ServiceModal)
          let message = service.lead?.ios;
          const whatsappTemplate = message.substring(0, message.indexOf('&text=') + 6);
          message = message.substring(message.indexOf('&text=') + 6);

          // Prepare form data with vehicle info
          const messageData: Record<string, string> = {
            nome: formData.name,
            name: formData.name,
            email: formData.email,
            telefone: formData.phone,
            phone: formData.phone,
            mensagem: formData.message,
            message: formData.message,
            veiculo: `${vehicle.brand} ${vehicle.model} ${vehicle.version ? `${vehicle.version} ` : ''}${vehicle.manufactureYear}/${vehicle.modelYear}`,
            vehicle: `${vehicle.brand} ${vehicle.model} ${vehicle.version ? `${vehicle.version} ` : ''}${vehicle.manufactureYear}/${vehicle.modelYear}`,
            marca: vehicle.brand,
            brand: vehicle.brand,
            modelo: vehicle.model,
            model: vehicle.model,
            versao: vehicle.version || '',
            version: vehicle.version || '',
            ano: `${vehicle.manufactureYear}/${vehicle.modelYear}`,
            year: `${vehicle.manufactureYear}/${vehicle.modelYear}`,
            preco: formatPrice(vehicle.price),
            price: formatPrice(vehicle.price),
            km: `${vehicle.mileage}km`,
            mileage: `${vehicle.mileage}km`
          };

          // Replace placeholders with form data
          Object.entries(messageData).forEach(([key, value]) => {
            const regex = new RegExp(`\\{${key}\\}`, 'g');
            message = message.replace(regex, value);
          });

          // Add current date/time if needed
          const now = new Date();
          message = message.replace(/\{datahora\}/g, now.toLocaleString('pt-BR'));
          message = message.replace(/\{datetime\}/g, now.toLocaleString('pt-BR'));

          const whatsappUrl = whatsappTemplate + encodeURIComponent(message);
          openWhatsApp(whatsappUrl);
        } else {
          // Use default WhatsApp with message
          const phoneMatch = store.whatsapp?.ios?.match(/phone=([^&]+)/);
          const phone = phoneMatch ? phoneMatch[1] : '';
          openWhatsApp(`whatsapp://send?phone=${phone}&text=${encodeURIComponent(formData.message)}`);
        }

        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: `Olá,\n\nTenho interesse no veículo:\n\n${vehicle.brand} ${vehicle.model} ${vehicle.version} ${vehicle.modelYear}\nPreço: ${formatPrice(vehicle.price)}\n\nGostaria de receber mais informações.`
        });
      }
    } catch (error: any) {
      console.error('Error submitting lead:', error);

      toast({
        title: 'Erro ao enviar',
        description: error?.message || 'Ocorreu um erro ao enviar sua mensagem. Tente novamente.',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-3">

        <CardTitle className="flex items-center gap-2">
          {serviceType === 'message' ? (
            <MessageCircle className="h-5 w-5 text-primary" />
          ) : (
            <CreditCard className="h-5 w-5 text-primary" />
          )}
          {serviceTitle}
        </CardTitle>

        {/* Name Field */}
        <div className="space-y-2">
          <Label htmlFor="name" className="text-sm font-semibold">
            Nome Completo <span className="text-red-500">*</span>
          </Label>
          <Input
            id="name"
            placeholder="Digite seu nome completo"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            className="h-11"
          />
        </div>

        {/* Email Field */}
        <div className="space-y-2">
          <Label htmlFor="email" className="text-sm font-semibold">
            E-mail <span className="text-red-500">*</span>
          </Label>
          <Input
            id="email"
            type="email"
            placeholder="seu@email.com"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            className="h-11"
          />
        </div>

        {/* Phone Field */}
        <div className="space-y-2">
          <Label htmlFor="phone" className="text-sm font-semibold">
            Telefone <span className="text-red-500">*</span>
          </Label>
          <Input
            id="phone"
            type="tel"
            placeholder="(11) 99999-9999"
            value={formData.phone}
            onChange={(e) => handlePhoneChange(e.target.value)}
            maxLength={15}
            className="h-11"
          />
        </div>

        {/* Message Field */}
        <div className="space-y-2">
          <Label htmlFor="message" className="text-sm font-semibold">
            Mensagem <span className="text-red-500">*</span>
          </Label>
          <Textarea
            id="message"
            placeholder="Digite sua mensagem..."
            value={formData.message}
            onChange={(e) => handleInputChange('message', e.target.value)}
            rows={5}
            className="resize-none"
          />
        </div>

        {/* Submit Button */}
        <Button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="w-full h-12 text-base font-semibold text-white shadow-lg hover:shadow-xl transition-all duration-300"
          style={{
            background: `linear-gradient(to right, ${buttonColor}, ${buttonColor}dd)`,
          }}
          onMouseEnter={(e) => {
            if (!isSubmitting) {
              e.currentTarget.style.background = `linear-gradient(to right, ${buttonColor}ee, ${buttonColor})`;
            }
          }}
          onMouseLeave={(e) => {
            if (!isSubmitting) {
              e.currentTarget.style.background = `linear-gradient(to right, ${buttonColor}, ${buttonColor}dd)`;
            }
          }}
        >
          {isSubmitting ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
              Enviando...
            </>
          ) : (
            <>
              <Send className="h-5 w-5 mr-2" />
              Enviar
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

const VehicleDetail = () => {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { config, vehicles, isLoading } = useAppData();
  const { selectedStore, availableStores } = useStore();

  // Extract slug from params (can be :id or carro/*, moto/*, caminhao/*)
  const slugOrId = params.id || params['*'] || '';
  const [vehicle, setVehicle] = useState<any>(null);
  const [isFavorited, setIsFavorited] = useState(false);
  const [relatedVehicles, setRelatedVehicles] = useState<any[]>([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [relatedSlugs, setRelatedSlugs] = useState<Record<number, string>>({});
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const { colors } = useTheme();
  const { toast } = useToast();

  const buttonColor = colors.button;
  const primaryColor = colors.primary;
  const secondaryColor = colors.secondary;
  const titleColor = colors.title;
  const textColor = colors.text;

  // Get the store for this vehicle (based on vehicle.store field)
  const vehicleStore = useMemo(() => {
    if (!vehicle) {
      return selectedStore || config.stores[0];
    }
    const storeId = vehicle.store;
    if (!storeId) {
      return selectedStore || config.stores[0];
    }
    return config.stores?.find((s: any) => s._id === storeId) || config.stores[0];
  }, [vehicle, config.stores, selectedStore]);

  // Request user location only when needed (user interaction)
  // Don't request automatically on page load to avoid trust issues

  useEffect(() => {
    const loadVehicleData = async () => {
      // Only check for vehicle after data has loaded
      if (!isLoading && vehicles.length > 0) {
        // Extract ID from slug or use direct ID
        const vehicleId = await extractIdFromSlug(slugOrId || '') || slugOrId;
        const foundVehicle = vehicles.find(v => v.id.toString() === vehicleId);
    if (foundVehicle) {
      setVehicle(foundVehicle);
      
          // Track vehicle view
          trackVehicleView({
            id: foundVehicle.id,
            brand: foundVehicle.brand,
            model: foundVehicle.model,
            version: foundVehicle.version || '',
            year: `${foundVehicle.manufactureYear}/${foundVehicle.modelYear}`,
            price: foundVehicle.price,
            mileage: foundVehicle.mileage
          });

          // Register vehicle as recently viewed
          try {
            const viewedStr = await secureGetItem('recentlyViewedVehicles');
            let viewedVehicles = viewedStr ? JSON.parse(viewedStr) : [];

            // Remove if already exists (to avoid duplicates)
            viewedVehicles = viewedVehicles.filter((v: any) => v.id !== foundVehicle.id);

            // Add to beginning of array with timestamp
            viewedVehicles.unshift({
              id: foundVehicle.id,
              timestamp: Date.now()
            });

            // Keep only last 20 vehicles
            viewedVehicles = viewedVehicles.slice(0, 20);

            await secureSetItem('recentlyViewedVehicles', JSON.stringify(viewedVehicles));

            // Dispatch event to notify other components
            window.dispatchEvent(new Event('vehicleViewed'));
          } catch (error) {
            console.error('Erro ao registrar veículo visualizado:', error);
          }

          // Check if vehicle is favorited
          const favorites = await secureGetItem('favorites');
          if (favorites && Array.isArray(favorites)) {
            setIsFavorited(favorites.includes(foundVehicle.id));
          }

          // Determine if we should filter by store based on unifiedAds and unifiedAdsStore
          const unifiedAds = config?.unifiedAds !== false;
          const unifiedAdsStore = config?.unifiedAdsStore;
          const vehicleStoreId = foundVehicle.store;

          let filterByStore = false;

          if (!unifiedAds) {
            // Rule 1: unifiedAds is false → always filter by vehicle's store
            filterByStore = true;
          } else if (unifiedAdsStore !== null && unifiedAdsStore !== undefined) {
            // Rule 3 & 4: unifiedAds is true and unifiedAdsStore is set
            // Only show all vehicles if vehicle's store is the unifiedAdsStore
            filterByStore = vehicleStoreId !== unifiedAdsStore;
          }
          // Rule 2: unifiedAds is true and unifiedAdsStore is null → don't filter (filterByStore = false)

          // Find related vehicles with priority: 1) same model, 2) same brand, 3) similar price
          // Filter by store if needed based on unifiedAds rules
          const sameModel = vehicles.filter(v =>
            v.id !== foundVehicle.id &&
            v.model === foundVehicle.model &&
            (!filterByStore || v.store === vehicleStoreId)
          );

          const sameBrand = vehicles.filter(v =>
            v.id !== foundVehicle.id &&
            v.brand === foundVehicle.brand &&
            v.model !== foundVehicle.model &&
            (!filterByStore || v.store === vehicleStoreId)
          );

          // Others: similar price range (±10,000)
          const priceMin = foundVehicle.price - 10000;
          const priceMax = foundVehicle.price + 10000;
          const others = vehicles.filter(v =>
            v.id !== foundVehicle.id &&
            v.brand !== foundVehicle.brand &&
            v.price >= priceMin &&
            v.price <= priceMax &&
            (!filterByStore || v.store === vehicleStoreId)
          );

          // Show only the highest priority available (don't mix)
          let related: any[] = [];
          if (sameModel.length > 0) {
            // Priority 1: Show only same model
            related = sameModel.slice(0, 4);
          } else if (sameBrand.length > 0) {
            // Priority 2: Show only same brand
            related = sameBrand.slice(0, 4);
          } else {
            // Priority 3: Show only similar price
            related = others.slice(0, 4);
          }

      setRelatedVehicles(related);
    }
        setHasLoaded(true);
      }
    };

    loadVehicleData();
  }, [slugOrId, vehicles, isLoading]);

  // Load slugs for related vehicles
  useEffect(() => {
    const loadRelatedSlugs = async () => {
      const newSlugs: Record<number, string> = {};
      for (const relatedVehicle of relatedVehicles) {
        newSlugs[relatedVehicle.id] = await generateVehicleSlug(relatedVehicle);
      }
      setRelatedSlugs(newSlugs);
    };
    if (relatedVehicles.length > 0) {
      loadRelatedSlugs();
    }
  }, [relatedVehicles]);

  // Show loading state
  if (isLoading || !hasLoaded) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Carregando...</p>
        </div>
      </div>
    );
  }

  // Show not found only after loading is complete
  if (!vehicle && hasLoaded) {
    return (
      <VehicleNotFound
        config={config}
        buttonColor={buttonColor}
        primaryColor={primaryColor}
        secondaryColor={secondaryColor}
        titleColor={titleColor}
        textColor={textColor}
      />
    );
  }

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price);
  };

  const formatMileage = (mileage: number) => {
    if (mileage === 0) return 'Zero KM';
    return new Intl.NumberFormat('pt-BR').format(mileage) + ' km';
  };

  const toggleFavorite = async () => {
    const favorites = await secureGetItem('favorites') || [];
    let updatedFavorites = Array.isArray(favorites) ? [...favorites] : [];

    if (updatedFavorites.includes(vehicle.id)) {
      // Remove from favorites
      updatedFavorites = updatedFavorites.filter((id: number) => id !== vehicle.id);
      setIsFavorited(false);
    } else {
      // Add to favorites
      updatedFavorites.push(vehicle.id);
      setIsFavorited(true);
      
      // Track add to favorites
      trackAddToFavorites({
        id: vehicle.id,
        brand: vehicle.brand,
        model: vehicle.model,
        version: vehicle.version || '',
        year: `${vehicle.manufactureYear}/${vehicle.modelYear}`,
        price: vehicle.price,
        mileage: vehicle.mileage
      });
    }

    await secureSetItem('favorites', updatedFavorites);

    // Dispatch custom event to update other components
    window.dispatchEvent(new Event('favoritesUpdated'));
  };

  const handleWhatsApp = () => {
    const adLead = vehicleStore.ad?.lead;

    // Check if lead type is "message" and has custom template
    if (adLead?.type === 'message' && adLead?.ios) {
      let message = adLead?.ios;
      message = message.substr(message.indexOf('&text=') + 6); // Extract message after 'text='

      // Replace dynamic placeholders based on replace array
      if (adLead.replace && Array.isArray(adLead.replace)) {
        adLead.replace.forEach((replaceItem: any) => {
          Object.keys(replaceItem).forEach((placeholder) => {
            const field = replaceItem[placeholder];
            let value = '';

            // Map field names to vehicle properties
            switch (field) {
              case 'vehicle':
                value = `${vehicle.brand} ${vehicle.model} ${vehicle.version} ${vehicle.modelYear}`;
                break;
              case 'price':
                value = formatPrice(vehicle.price);
                break;
              case 'brand':
                value = vehicle.brand;
                break;
              case 'model':
                value = vehicle.model;
                break;
              case 'version':
                value = vehicle.version;
                break;
              case 'year':
              case 'modelYear':
                value = vehicle.modelYear.toString();
                break;
              case 'mileage':
                value = `${vehicle.mileage.toLocaleString('pt-BR')} km`;
                break;
              case 'color':
                value = vehicle.color;
                break;
              case 'fuel':
                value = vehicle.fuel;
                break;
              case 'transmission':
                value = vehicle.transmission;
                break;
              default:
                // Try to get value directly from vehicle object
                value = vehicle[field] || '';
            }

            // Replace placeholder in message (case insensitive)
            const regex = new RegExp(`\\{${placeholder}\\}`, 'gi');
            message = message.replace(regex, value);
          });
        });
      }

      // Extract phone number from whatsapp?.ios URL
      const phoneMatch = vehicleStore.ad?.lead?.ios?.match(/phone=([^&]+)/);
      const phone = phoneMatch ? phoneMatch[1] : '';

      // Build WhatsApp URL
      const url = `whatsapp://send?phone=${phone}&text=${encodeURIComponent(message)}`;

      // Track WhatsApp click
      trackWhatsAppClick('vehicle_detail', {
        id: vehicle.id,
        brand: vehicle.brand,
        model: vehicle.model,
        version: vehicle.version || '',
        year: `${vehicle.manufactureYear}/${vehicle.modelYear}`,
        price: vehicle.price,
        mileage: vehicle.mileage
      });

      // Open WhatsApp with fallback to web version
      openWhatsApp(url);
    } else {
      // Default behavior (fallback)
      const message = `Olá,\n\nTenho interesse no veículo abaixo e gostaria de receber mais informações.\n\n${vehicle.brand} ${vehicle.model} ${vehicle.version}, ${formatPrice(vehicle.price)}\n\nFico no aguardo,`;
      const url = `${vehicleStore.ad?.lead?.ios}&text=${encodeURIComponent(message)}`;

      // Track WhatsApp click
      trackWhatsAppClick('vehicle_detail', {
        id: vehicle.id,
        brand: vehicle.brand,
        model: vehicle.model,
        version: vehicle.version || '',
        year: `${vehicle.manufactureYear}/${vehicle.modelYear}`,
        price: vehicle.price,
        mileage: vehicle.mileage
      });

      // Open WhatsApp with fallback to web version
      openWhatsApp(url);
    }
  };

  // Find financing service from vehicle's store
  const financingService = vehicleStore?.services?.find(
    (s: any) => s.service === '65dcb7898eca108d87c91dbc'
  );

  // Find test-drive service from vehicle's store
  const testDriveService = vehicleStore?.services?.find(
    (s: any) => s.service === '65dcb5e78eca108d87c91db8'
  );

  // Check if test-drive should be shown
  const showTestDrive = vehicleStore?.ad?.showTestDriveOption && testDriveService;

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${vehicle.brand} ${vehicle.model} ${vehicle.version}`,
          text: `Confira este ${vehicle.brand} ${vehicle.model} por ${formatPrice(vehicle.price)}`,
          url: window.location.href,
        });
      } catch (err) { }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      toast({
        title: '✅ Link copiado',
        description: 'O link do veículo foi copiado para a área de transferência.',
      });
    }
  };

  const shareUrl = `${window.location.origin}${location.pathname}${location.search}`;
  const canonicalVehicleUrl = `${window.location.origin}${location.pathname}`;
  const seoVehicleTitle = `${vehicle.brand} ${vehicle.model}${vehicle.version ? ` ${vehicle.version}` : ''} ${vehicle.modelYear}`;

  return (
    <Layout3Root withHeader className="bg-background">
      <SEOHead
        title={seoVehicleTitle}
        description={`${vehicle.brand} ${vehicle.model}${vehicle.version ? ` ${vehicle.version}` : ''} ${vehicle.modelYear} — ${vehicle.color} — ${vehicle.manufactureYear}/${vehicle.modelYear} — ${vehicle.fuel} — ${vehicle.transmission} — ${vehicle.mileage.toLocaleString('pt-BR')} km — ${formatPrice(vehicle.price)}`}
        keywords={`${vehicle.brand}, ${vehicle.model}, ${vehicle.modelYear}, ${vehicle.fuel}, ${vehicle.transmission}, ${vehicle.color}, veículo usado, carro seminovo`}
        image={vehicle.photos?.[0]}
        imageAlt={seoVehicleTitle}
        type="product"
        url={shareUrl}
        canonicalUrl={canonicalVehicleUrl}
        structuredData={generateVehicleStructuredData({
          vehicle,
          config,
          store: vehicleStore,
          vehiclePageUrl: shareUrl
        })}
      />
      <Header config={config} />
      
      {/* Breadcrumb with Back Button */}
      <section className="border-b py-4">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Link to="/" className="hover:text-foreground">Início</Link>
            <span>/</span>
            <Link to="/estoque" className="hover:text-foreground">Estoque</Link>
            <span>/</span>
            <span className="text-foreground">{vehicle.brand} {vehicle.model}</span>
          </div>

          {/* Back Button */}
          <Button 
            variant="outline" 
            onClick={() => navigate(-1)}
              size="sm"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar
          </Button>
          </div>
        </div>
      </section>

      <section className="py-8">
        <div className="container mx-auto px-4">

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Image Gallery */}
            <div className="lg:col-span-2">
              <VehicleGallery
                key={vehicle.id}
                photos={vehicle.photos}
                alt={`${vehicle.brand} ${vehicle.model}`}
                isZeroKm={vehicle.isZeroKm}
                isFavorited={isFavorited}
                onFavorite={toggleFavorite}
                onShare={handleShare}
              />

              {/* Vehicle Details */}
                <Card className="mt-6 shadow-card">
                {/* <CardHeader>
                  <CardTitle>Detalhes do Veículo</CardTitle>
                </CardHeader> */}
                  <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-6 mt-6">
                    {/* Ano - Always show */}
                    {(vehicle.manufactureYear || vehicle.modelYear) && (
                    <div className="flex items-center gap-3">
                      <Calendar className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <p className="text-sm text-muted-foreground">Ano</p>
                          <p className="font-medium">{vehicle.manufactureYear}/{vehicle.modelYear}</p>
                      </div>
                    </div>
                    )}

                    {/* KM - Always show */}
                    {vehicle.mileage !== undefined && vehicle.mileage !== null && (
                    <div className="flex items-center gap-3">
                      <Gauge className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <p className="text-sm text-muted-foreground">KM</p>
                        <p className="font-medium">{formatMileage(vehicle.mileage)}</p>
                      </div>
                    </div>
                    )}

                    {/* Fuel - Only if exists */}
                    {vehicle.fuel && (
                    <div className="flex items-center gap-3">
                      <Fuel className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <p className="text-sm text-muted-foreground">Combustível</p>
                        <p className="font-medium">{vehicle.fuel}</p>
                      </div>
                    </div>
                    )}

                    {/* Transmission - Only for Carro and Caminhão, and if exists */}
                    {vehicle.type !== 2 && vehicle.transmission && (
                    <div className="flex items-center gap-3">
                      <Settings className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <p className="text-sm text-muted-foreground">Câmbio</p>
                        <p className="font-medium">{vehicle.transmission}</p>
                      </div>
                    </div>
                    )}

                    {/* Color - Only if exists */}
                    {vehicle.color && (
                    <div className="flex items-center gap-3">
                      <Palette className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <p className="text-sm text-muted-foreground">Cor</p>
                        <p className="font-medium">{vehicle.color}</p>
                      </div>
                    </div>
                    )}

                    {/* Doors - Only for Carro and Caminhão, and if exists */}
                    {vehicle.type !== 2 && vehicle.doors && (
                    <div className="flex items-center gap-3">
                      <Car className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <p className="text-sm text-muted-foreground">Portas</p>
                        <p className="font-medium">{vehicle.doors}</p>
                      </div>
                    </div>
                    )}

                    {/* Store - Only show if multiple stores */}
                    {availableStores.length > 1 && vehicle.store && (() => {
                      const vehicleStore = config.stores?.find((s: any) => s._id === vehicle.store);
                      return vehicleStore ? (
                        <div className="flex items-center gap-3">
                          <Building2 className="h-5 w-5 text-muted-foreground" />
                          <div>
                            <p className="text-sm text-muted-foreground">Loja</p>
                            <p className="font-medium">{vehicleStore.company}</p>
                          </div>
                        </div>
                      ) : null;
                    })()}
                  </div>

                  {/* Statistics */}
                  {vehicle.statistics && (vehicle.statistics.financing || vehicle.statistics.favorites) && (
                    <>
                      <Separator className="my-6" />
                      <div className="flex items-center gap-6 text-sm text-muted-foreground">

                        {vehicle.statistics.financing && config.stores?.[0]?.ad?.displayNumberOfFinancingProposals && (
                          <div className="flex items-center gap-2">
                            <FilePen className="h-4 w-4" />
                            <span>{vehicle.statistics.financing > 1 ? `${vehicle.statistics.financing} propostas em análise` : `${vehicle.statistics.financing} proposta em análise`}</span>
                          </div>
                        )}

                        {vehicle.statistics.favorites && vehicleStore?.ad?.displayNumberOfInterestedCustomers && (
                          <div className="flex items-center gap-2">
                            <Heart className="h-4 w-4" />
                            <span>{vehicle.statistics.favorites > 1 ? `${vehicle.statistics.favorites} clientes interessados` : `${vehicle.statistics.favorites} cliente interessado`}</span>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Vehicle Description */}
              {vehicle.description && (
                <Card className="mt-6 shadow-card">
                  {/* <CardHeader>
                    <CardTitle>Descrição</CardTitle>
                  </CardHeader> */}
                  <CardContent>
                    <p className="text-muted-foreground leading-relaxed mt-6">
                      {vehicle.description}
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Optionals */}
              {vehicle.optionals && vehicle.optionals.length > 0 && (
                <Card className="mt-6 shadow-card">
                  <CardHeader>
                    <CardTitle>Opcionais ({vehicle.optionals.length})</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {vehicle.optionals.map((optional: string, index: number) => (
                        <div key={index} className="flex items-center gap-2 p-2 rounded">
                          <div className="w-1 h-1 bg-primary rounded-full" />
                          <span className="text-sm">{optional}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Financing Card - Inline Form (when financing service exists with type message) */}
              {financingService && financingService.lead?.type === 'message' && (
                <Card className="mt-6 shadow-card border-primary/20">
                  <CardContent className="p-6">
                    <ServiceModal
                      service={financingService}
                      serviceTitle="Simular Financiamento"
                      config={config}
                      renderInline={true}
                      preSelectedVehicle={vehicle}
                    />
                  </CardContent>
                </Card>
              )}

              {/* Financing Card - Inline Form (when financing service exists with type api) */}
              {financingService && (financingService.lead?.type === 'api' || financingService.lead?.type === 'email') && (
                <Card className="mt-6 shadow-card border-primary/20">
                  <CardContent className="p-6">
                    <LeadForm
                      vehicle={vehicle}
                      config={config}
                      buttonColor={buttonColor}
                      store={vehicleStore}
                      serviceType="65dcb7898eca108d87c91dbc"
                      serviceTitle="Simular Financiamento"
                      service={financingService}
                    />
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Vehicle Info & Contact */}
            <div className="space-y-6">
              {/* Main Info Card */}
              <Card className="shadow-card">
                <CardContent className="p-6">
                  {/* Title */}
                  <div className="mb-6">
                    <h1 className="text-2xl lg:text-3xl font-bold text-foreground mb-2" style={{ color: titleColor }}>
                      {vehicle.brand} {vehicle.model}
                    </h1>
                    <p className="text-lg text-muted-foreground" style={{ color: textColor }}>
                      {vehicle.version}
                    </p>
                  </div>

                  {/* Price */}
                  <div className="mb-6">
                    <div className="text-4xl font-bold text-primary mb-2">
                      {formatPrice(vehicle.price)}
                    </div>
                  </div>

                  {/* <Separator className="my-6" /> */}


                  {/* Action Buttons */}
                  <div className="space-y-3 border-t pt-6">


                    {financingService && (
                      <>
                        {/* <ServiceModal
                          service={financingService}
                          config={config}
                          preSelectedVehicle={vehicle}
                        >
                    <Button 
                      variant="outline" 
                            className="w-full border-2 transition-all duration-300 flex-col"
                            style={{
                              borderColor: buttonColor,
                              color: buttonColor,
                              background: 'transparent',
                              display: 'block',
                              height: '60px',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = buttonColor;
                              e.currentTarget.style.color = 'white';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'transparent';
                              e.currentTarget.style.color = buttonColor;
                            }}
                      size="lg"
                    >
                            <span className="flex w-full">
                              <CreditCard className="h-5 w-5 mr-2" />
                              Financiamento
                            </span>
                            <span style={{ fontSize: '0.75rem', fontWeight: 'normal' }} className='flex w-full'>
                              Clique aqui para simular seu financiamento
                            </span>
                    </Button>
                        </ServiceModal> */}

                        {/* Statistics */}
                        {/* {vehicle.statistics && vehicle.statistics.financing && config.stores?.[0]?.ad?.displayNumberOfFinancingProposals && (
                          <>
                            <div className="flex items-center gap-6 text-sm text-muted-foreground">
                              {vehicle.statistics.financing && (
                                <div className="flex items-center gap-2">
                                  <FilePen className="h-4 w-4" />
                                  <span>{vehicle.statistics.financing > 1 ? `${vehicle.statistics.financing} propostas em análise` : `${vehicle.statistics.financing} proposta em análise`}</span>
                    </div>
                              )}
                            </div>
                          </>
                        )} */}
                      </>
                    )}

                    {/* Lead Section - Form for API, Button for Message */}
                    {(vehicleStore.ad?.lead?.type === 'api' || vehicleStore.ad?.lead?.type === 'email') ? (
                      <LeadForm
                        vehicle={vehicle}
                        config={config}
                        buttonColor={buttonColor}
                        store={vehicleStore}
                        serviceType="message"
                        serviceTitle="Envie uma mensagem"
                        service={{ lead: vehicleStore.ad.lead }} />
                    ) : (
                      <Button
                        className="w-full text-white hover:shadow-glow transition-all duration-300"
                        style={{
                          background: `linear-gradient(to right, ${buttonColor}, ${buttonColor}dd)`,
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = `linear-gradient(to right, ${buttonColor}ee, ${buttonColor})`;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = `linear-gradient(to right, ${buttonColor}, ${buttonColor}dd)`;
                        }}
                        onClick={handleWhatsApp}
                        size="lg"
                      >
                        <MessageCircle className="h-5 w-5 mr-2" />
                        Enviar mensagem
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Guarantee Badge */}
              <Card
                className="shadow-card text-white"
                style={{
                  background: `linear-gradient(135deg, ${secondaryColor}, ${secondaryColor}dd)`
                }}
              >
                <CardContent className="p-6 text-center">
                  <Shield className="h-12 w-12 mx-auto mb-3 opacity-90" />
                  <h3 className="font-bold mb-2">Garantia e Qualidade</h3>
                  <p className="text-sm opacity-90">
                    Todos os nossos veículos passam por rigorosa inspeção e vêm com garantia.
                  </p>
                </CardContent>
              </Card>

              {/* Store Info - Enhanced Design */}
              <Card className="shadow-card overflow-hidden border-primary/20">
                <CardContent className="pt-6 pb-6">
                  {/* Test-Drive Card */}
                  {showTestDrive && (
                    <div className="text-center mb-6">
                      <CardTitle className="flex items-center gap-2 mb-4">
                        <Gauge className="h-5 w-5 text-primary" />
                        Test-Drive
                      </CardTitle>

                      <ServiceModal
                        service={testDriveService}
                        serviceTitle="Test-Drive"
                        config={config}
                        preSelectedVehicle={vehicle}
                      >
                        <Button
                          className="w-full text-white"
                          style={{
                            background: `linear-gradient(to right, ${buttonColor}, ${buttonColor}dd)`,
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = `linear-gradient(to right, ${buttonColor}ee, ${buttonColor})`;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = `linear-gradient(to right, ${buttonColor}, ${buttonColor}dd)`;
                          }}
                        >
                          Agendar
                        </Button>
                      </ServiceModal>
                      <p className="text-sm text-muted-foreground mt-4 mb-4">
                        Agende um test drive e experimente o veículo dos seus sonhos
                      </p>
                      <Separator className="mb-6" />
                    </div>
                  )}

                  {/* Store Information */}
                  {availableStores.length > 1 && vehicleStore?.company && (
                    <div className="mb-6 p-4 bg-muted/30 rounded-lg border border-border">
                      <div className="space-y-2 text-sm">
                        {/* Store Name + Distance Badge */}
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <span className="font-medium">{vehicleStore.company}</span>
                          {availableStores.length > 1 && userLocation && (() => {
                            const distance = getDistanceToStore(userLocation, vehicleStore);
                            return distance !== null ? (
                              <Badge
                                variant="secondary"
                                className="flex items-center gap-1 px-2 py-0.5 text-white font-medium flex-shrink-0"
                                style={{ backgroundColor: primaryColor }}
                              >
                                <Navigation className="h-3 w-3" />
                                <span className="text-xs">{formatDistance(distance)}</span>
                              </Badge>
                            ) : null;
                          })()}
                        </div>

                        {/* Address */}
                        <div className="flex items-start gap-2">
                          <MapPin className="h-4 w-4 mt-0.5 text-muted-foreground flex-shrink-0" />
                          <span className="text-muted-foreground">{vehicleStore.place.address}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="space-y-3">
                    {/* WhatsApp Button */}
                    <Button
                      onClick={() => {
                        if (vehicleStore?.whatsapp?.ios) {
                          openWhatsApp(vehicleStore.whatsapp?.ios);
                        }
                      }}
                      className="w-full bg-green-600 hover:bg-green-700 text-white shadow-md hover:shadow-lg transition-all duration-300"
                      size="lg"
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-1px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      <MessageCircle className="h-5 w-5 mr-2" />
                      WhatsApp
                    </Button>

                    {/* Call Button */}
                    <Button
                      onClick={() => {
                        if (vehicleStore?.phone?.ios) {
                          window.open(vehicleStore.phone?.ios, '_blank');
                        }
                      }}
                      className="w-full text-white shadow-md hover:shadow-lg transition-all duration-300"
                      style={{
                        background: `linear-gradient(to right, ${buttonColor}, ${buttonColor}dd)`,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = `linear-gradient(to right, ${buttonColor}ee, ${buttonColor})`;
                        e.currentTarget.style.transform = 'translateY(-1px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = `linear-gradient(to right, ${buttonColor}, ${buttonColor}dd)`;
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                      size="lg"
                    >
                      <Phone className="h-5 w-5 mr-2" />
                      Ligar Agora
                    </Button>

                    {/* Directions Button */}
                    <Button
                      variant="outline"
                      size="lg"
                      className="w-full border-2 transition-all duration-300 shadow-sm hover:shadow-md"
                      style={{
                        borderColor: `${primaryColor}40`,
                        color: primaryColor,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = primaryColor;
                        e.currentTarget.style.backgroundColor = `${primaryColor}10`;
                        e.currentTarget.style.transform = 'translateY(-1px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = `${primaryColor}40`;
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                      onClick={() => {
                        const place = vehicleStore?.place;
                        openStoreDirections(place);
                      }}
                    >
                      <MapPin className="h-5 w-5 mr-2" />
                      Como Chegar
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Related Vehicles */}
          {vehicleStore?.ad?.showRelatedOffers && relatedVehicles.length > 0 && (
            <section className="mt-16">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* Real Vehicles */}
                {relatedVehicles.slice(0, 4).map((relatedVehicle) => {
                  const relatedSlug = relatedSlugs[relatedVehicle.id] || '';
                  return (
                  <Link 
                    key={relatedVehicle.id} 
                      to={`/estoque/${relatedSlug}`}
                    className="block group"
                  >
                      <Card className="overflow-hidden shadow-card hover:shadow-elegant transition-all duration-300">
                      <div className="relative h-48">
                        <img
                          src={relatedVehicle.photos[0]}
                          alt={`${relatedVehicle.brand} ${relatedVehicle.model}`}
                          className="w-full h-full object-cover transition-transform duration-300"
                        />
                        {relatedVehicle.isZeroKm && (
                          <Badge className="absolute top-2 left-2 bg-accent text-accent-foreground">
                            Zero KM
                          </Badge>
                        )}
                      </div>
                      <CardContent className="p-4">
                        <h3 className="font-bold text-lg mb-1">
                          {relatedVehicle.brand} {relatedVehicle.model}
                        </h3>
                        <p className="text-sm text-muted-foreground mb-3">
                          {relatedVehicle.version}
                        </p>
                        <div className="text-xl font-bold text-primary">
                          {formatPrice(relatedVehicle.price)}
                        </div>
                        <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                            <span>{relatedVehicle.manufactureYear}/{relatedVehicle.modelYear}</span>
                          <span>{formatMileage(relatedVehicle.mileage)}</span>
                          <span>{relatedVehicle.fuel}</span>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                  );
                })}

                {/* Placeholders - Only on desktop (md and up) */}
                {relatedVehicles.length < 4 && Array.from({ length: 4 - relatedVehicles.length }).map((_, index) => (
                  <Card key={`placeholder-${index}`} className="hidden md:block overflow-hidden border-dashed border-border/50 bg-muted/20">
                    <CardContent className="p-0">
                      {/* Placeholder Image */}
                      <div className="relative h-48 overflow-hidden bg-muted/30 flex items-center justify-center">
                        <Eye className="h-8 w-8 text-muted-foreground/30" />
                      </div>

                      {/* Placeholder Info */}
                      <div className="p-4">
                        <div className="space-y-2">
                          <div className="h-5 bg-muted/50 rounded w-3/4"></div>
                          <div className="h-4 bg-muted/30 rounded w-1/2"></div>
                          <div className="h-5 bg-muted/40 rounded w-2/3"></div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>
          )}

          {/* Warning */}
          {config.stores?.[0]?.ad?.warning && (
            <div className="mt-12 mb-8">
              <Card className="border-yellow-200 bg-yellow-50/50">
                <CardContent className="p-6">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      <svg className="h-5 w-5 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-semibold text-yellow-900 mb-1">Aviso</h3>
                      <p className="text-sm text-yellow-800 leading-relaxed">
                        {vehicleStore.ad.warning}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </section>
      
      <Footer config={config} />
    </Layout3Root>
  );
};

export default VehicleDetail;